import { useCallback, useEffect, useRef, useState } from "react";
import { imageUrl } from "../../utils/imageUrl";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { productUrl } from "../../utils/productUrl";
import { BsTrash } from "react-icons/bs";
import { FiShoppingBag, FiArrowLeft, FiHeart } from "react-icons/fi";
import { motion } from "framer-motion";
import Nav from "../../components/home/Nav";
import { getFinalPrice } from "../../utils/productPrice";
import Quantity from "../../components/home/Quantity";
import { incQuantity, decQuantity, removeItem, syncCart } from "../../store/reducers/cartReducer";
import { toPersianNumber } from "../../utils/persian";
import Money from "../../components/ui/Money";
import {
  useAbandonPaymentRecoveryMutation,
  useCheckSnappEligibleQuery,
  usePaymentSettingsQuery
} from "../../store/services/paymentService";
import { useFeedback } from "../../context/AppFeedbackContext";
import { useValidateCartMutation } from "../../store/services/checkoutService";
import { trackAnalytics } from "../../utils/analytics";
import { clearPendingPayment, pendingPaymentOrderRef } from "../../utils/pendingPayment";

const snappPayLogo = "/images/payment/snapp-pay/40x40@3x.png";

const getCartItemImage = (item) =>
  item?.image1 || item?.main_image || item?.image || item?.thumbnail || item?.gallery?.[0] || "";

const Cart = () => {
  const { cart, total, items } = useSelector((state) => state.cartReducer);
  const { userToken } = useSelector((state) => state.authReducer);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { confirm, notify } = useFeedback();
  const [validateCart, validateCartResponse] = useValidateCartMutation();
  const [
    abandonPaymentRecovery,
    abandonPaymentRecoveryResponse
  ] = useAbandonPaymentRecoveryMutation();
  const paymentSettingsResponse = usePaymentSettingsQuery();
  const snappPayEnabled = Boolean(paymentSettingsResponse.data?.snapp_pay?.enabled);
  const snappEligibilityResponse = useCheckSnappEligibleQuery(Number(total || 0), {
    skip: !snappPayEnabled || Number(total || 0) <= 0,
    refetchOnMountOrArgChange: true
  });
  const snappEligibility = snappEligibilityResponse.data || {};
  const snappEligibleNested = snappEligibility.response?.response || snappEligibility.response || {};
  const snappMinimumAmount = Number(snappEligibility.minimum_amount_toman || 4000);
  const snappPayEligible = Boolean(snappEligibilityResponse.isSuccess && snappEligibility.eligible);
  const snappPayTitle =
    snappEligibility.title_message ||
    snappEligibleNested.title_message ||
    "پرداخت قسطی و اعتباری با اسنپ‌پی";
  const snappPayDescription =
    snappEligibility.description ||
    snappEligibleNested.description ||
    "۴ قسط بدون کارمزد";
  const shouldShowSnappPay =
    snappPayEnabled &&
    Number(total || 0) >= snappMinimumAmount &&
    snappPayEligible;
  const cartValidationSignatureRef = useRef("");
  const [paymentRecovery, setPaymentRecovery] = useState(null);

  const blockRecoveryEdit = () => {
    if (!paymentRecovery?.order_ref) {
      return false;
    }

    notify(
      paymentRecovery?.can_abandon
        ? "برای تغییر سبد، ابتدا گزینه «تغییر سبد» را انتخاب کنید."
        : "تا مشخص شدن وضعیت پرداخت فعلی، امکان تغییر سبد وجود ندارد.",
      "info",
      "سبد خرید"
    );

    return true;
  };

  const inc = (item) => {
    if (blockRecoveryEdit()) {
      return;
    }
    const stock = Number(item.stock || item.available_stock || 0);

    if (stock > 0 && Number(item.quantity || 0) >= stock) {
      return;
    }

    dispatch(
      incQuantity({
        _id: item._id,
        variant_id: item.variant_id || item.product_variant_id || null,
        product_variant_id: item.product_variant_id || item.variant_id || null,
        color: item.color,
        size: item.size
      })
    );
  };

  const dec = (item) => {
    if (blockRecoveryEdit()) {
      return;
    }

    dispatch(
      decQuantity({
        _id: item._id,
        variant_id: item.variant_id || item.product_variant_id || null,
        product_variant_id: item.product_variant_id || item.variant_id || null,
        color: item.color,
        size: item.size
      })
    );
  };

  const notifyCartSync = useCallback((res) => {
    const summary = res?.summary || {};
    const removedCount = Number(summary.removed_count || 0);
    const quantityChangedCount = Number(summary.quantity_changed_count || 0);

    const priceChangedCount = Number(summary.price_changed_count || 0);

    if (removedCount > 0) {
      notify("برخی کالاهای غیرفعال یا ناموجود از سبد خرید حذف شدند.", "warning", "سبد خرید");
      return;
    }

    if (quantityChangedCount > 0) {
      notify("تعداد برخی کالاها با موجودی جدید به‌روز شد.", "warning", "سبد خرید");
      return;
    }

    if (priceChangedCount > 0) {
      notify("قیمت برخی کالاها به‌روز شد.", "warning", "سبد خرید");
    }
  }, [notify]);

  const checkCartBeforeCheckout = async () => {
    const requestedRecoveryOrderRef = pendingPaymentOrderRef(cart);

    const res = await validateCart({
      cart,
      ...(requestedRecoveryOrderRef
        ? { recovery_order_ref: requestedRecoveryOrderRef }
        : {})
    }).unwrap();

    const recoveryOrderRef = String(res?.recovery_order_ref || "").trim() || null;

    setPaymentRecovery(
      recoveryOrderRef && res?.recovery
        ? res.recovery
        : null
    );

    if (requestedRecoveryOrderRef && !recoveryOrderRef) {
      clearPendingPayment(requestedRecoveryOrderRef);
    }

    const nextCart = Array.isArray(res.cart) ? res.cart : cart;

    if (Array.isArray(res.cart)) {
      dispatch(syncCart(res.cart));
    }

    notifyCartSync(res);

    if (!nextCart.length) {
      return null;
    }

    return {
      cart: nextCart,
      recoveryOrderRef
    };
  };

  useEffect(() => {
    if (!cart.length) {
      cartValidationSignatureRef.current = "";
      setPaymentRecovery(null);
      return;
    }

    const signature = JSON.stringify(
      cart.map((item) => ({
        id: item._id,
        variant_id: item.variant_id || item.product_variant_id || null,
        quantity: item.quantity,
        price: item.price,
        sale_price: item.sale_price,
        discount: item.discount || item.discount_percent || 0
      }))
    );

    if (cartValidationSignatureRef.current === signature) {
      return;
    }

    cartValidationSignatureRef.current = signature;

    let cancelled = false;
    const requestedRecoveryOrderRef = pendingPaymentOrderRef(cart);

    validateCart({
      cart,
      ...(requestedRecoveryOrderRef
        ? { recovery_order_ref: requestedRecoveryOrderRef }
        : {})
    })
      .unwrap()
      .then((res) => {
        if (cancelled) return;

        if (requestedRecoveryOrderRef && !res?.recovery_order_ref) {
          clearPendingPayment(requestedRecoveryOrderRef);
        }

        setPaymentRecovery(
          res?.recovery_order_ref && res?.recovery
            ? res.recovery
            : null
        );

        if (Array.isArray(res.cart)) {
          dispatch(syncCart(res.cart));
        }

        const summary = res?.summary || {};
        const hasChange =
          Number(summary.removed_count || 0) > 0 ||
          Number(summary.quantity_changed_count || 0) > 0 ||
          Number(summary.price_changed_count || 0) > 0;

        if (hasChange) {
          notifyCartSync(res);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [cart, dispatch, notifyCartSync, validateCart]);

  const remove = async (item) => {
    if (blockRecoveryEdit()) {
      return;
    }

    const accepted = await confirm({
      title: "حذف محصول از سبد",
      message: `آیا می‌خواهید «${item.title || item.name || "این محصول"}» از سبد خرید حذف شود؟`,
      confirmText: "حذف محصول",
      cancelText: "انصراف",
      variant: "danger"
    });

    if (!accepted) return;

    dispatch(
      removeItem({
        _id: item._id,
        variant_id: item.variant_id || item.product_variant_id || null,
        product_variant_id: item.product_variant_id || item.variant_id || null,
        color: item.color,
        size: item.size
      })
    );

    notify("محصول حذف شد.", "success");
  };

  const pay = async () => {
    if (!cart.length || validateCartResponse.isLoading) return;

    let checkoutState;

    try {
      checkoutState = await checkCartBeforeCheckout();

      if (!checkoutState) return;
    } catch (err) {
      notify(err?.data?.message || "سبد بررسی نشد.", "error", "سبد بررسی نشد");
      return;
    }

    const recoveryOrderRef = checkoutState.recoveryOrderRef;

    trackAnalytics("checkout_start", {
      metadata: {
        source: recoveryOrderRef ? "cart_payment_recovery" : "cart",
        items_count: Number(items || 0),
        cart_total: Number(total || 0)
      }
    });

    if (userToken) {
      navigate("/checkout");
    } else {
      sessionStorage.setItem("redirectAfterLogin", "/checkout");
      navigate("/login", { state: { from: "/checkout" } });
    }
  };

  const originalTotal = cart.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);
  const savedAmount = Math.max(originalTotal - total, 0);
  const hasAnyDiscount = savedAmount > 0;

  const recoveryOrderRef =
    String(paymentRecovery?.order_ref || "").trim();

  const hasPaymentRecovery =
    Boolean(recoveryOrderRef);

  const recoveryRequiresReview =
    Boolean(paymentRecovery?.requires_review);

  const reservationUntilLabel = (() => {
    if (!paymentRecovery?.reserved_until) {
      return "";
    }

    const date =
      new Date(paymentRecovery.reserved_until);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return `رزرو تا ${new Intl.DateTimeFormat(
      "fa-IR",
      {
        hour: "2-digit",
        minute: "2-digit"
      }
    ).format(date)}`;
  })();

  const openRecoveryStatus = () => {
    if (!recoveryOrderRef) {
      return;
    }

    navigate(
      `/payment/result?status=pending&order_ref=${encodeURIComponent(
        recoveryOrderRef
      )}`
    );
  };

  const changeRecoveryCart = async () => {
    if (
      !recoveryOrderRef ||
      !paymentRecovery?.can_abandon ||
      abandonPaymentRecoveryResponse.isLoading
    ) {
      return;
    }

    const accepted = await confirm({
      title: "تغییر سبد خرید",
      message:
        "رزرو سفارش نیمه‌تمام آزاد می‌شود و سپس می‌توانید کالاها، تعداد، رنگ یا سایز را تغییر دهید.",
      confirmText: "تغییر سبد",
      cancelText: "انصراف"
    });

    if (!accepted) {
      return;
    }

    try {
      await abandonPaymentRecovery(
        recoveryOrderRef
      ).unwrap();

      clearPendingPayment(
        recoveryOrderRef
      );

      setPaymentRecovery(null);

      cartValidationSignatureRef.current = "";

      notify(
        "اکنون می‌توانید سبد خرید را تغییر دهید.",
        "success",
        "سبد خرید"
      );
    } catch (error) {
      notify(
        error?.data?.message ||
          "در حال حاضر امکان تغییر امن سبد وجود ندارد.",
        "warning",
        "سبد خرید"
      );
    }
  };

  return (
    <>
      <Nav />

      <motion.main initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="cart-page cart-page-fashion">
        <div className="site-container">
          <div className="cart-fashion-hero">
            <div>
              <span className="cart-fashion-kicker">سبد خرید</span>
              <h1>سبد خرید</h1>

            </div>

            {cart.length > 0 && (
              <div className="cart-fashion-mini">
                <FiHeart size={20} />
                <strong>{toPersianNumber(items)}</strong>
                <span>کالا در سبد شماست</span>
              </div>
            )}
          </div>

          {cart.length > 0 ? (
            <div className="cart-fashion-layout">
              <section className="cart-fashion-list">
                {cart.map((item, index) => {
                  const itemPrice = getFinalPrice(item);
                  const originalPrice = Number(item.price || 0);
                  const rowTotal = itemPrice * item.quantity;
                  const originalRowTotal = originalPrice * item.quantity;
                  const hasDiscount = itemPrice > 0 && itemPrice < originalPrice;
                  const key = `${item._id}-${item.color || "no-color"}-${item.size || "no-size"}`;

                  return (
                    <motion.article
                      className="cart-fashion-item"
                      key={key}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.04 }}
                    >
                      <Link to={productUrl(item)} className="cart-fashion-image">
                        <img src={imageUrl(getCartItemImage(item))} alt={item.title || item.name || ""} />
                        {hasDiscount && <span>{toPersianNumber(item.discount)}٪ تخفیف</span>}
                      </Link>

                      <div className="cart-fashion-info">
                        <div className="cart-fashion-title-row">
                          <Link to={productUrl(item)} className="cart-fashion-title">
                            {item.title || item.name}
                          </Link>

                          <button type="button" className="cart-fashion-remove" onClick={() => remove(item)} aria-label="حذف محصول">
                            <BsTrash size={17} />
                          </button>
                        </div>

                        <div className="cart-fashion-meta">
                          <span>رنگ: {item.color || "انتخاب نشده"}</span>
                          <span>سایز: {item.size ? toPersianNumber(item.size) : "انتخاب نشده"}</span>

                          {hasPaymentRecovery ? (
                            <span className="cart-recovery-badge">
                              رزرو برای شما
                            </span>
                          ) : (
                            Number(item.stock || item.available_stock || 0) > 0 && (
                              <span>موجود</span>
                            )
                          )}
                        </div>

                        <div className="cart-fashion-bottom">
                          <div className="cart-fashion-price">
                            {hasDiscount && <del><Money value={originalPrice} /></del>}
                            <strong className={hasDiscount ? "cart-positive-price" : ""}><Money value={itemPrice} /></strong>
                            <small>
                              جمع:
                              <b className={hasDiscount ? "cart-positive-price" : ""}><Money value={rowTotal} /></b>
                            </small>
                            {hasDiscount && <em>صرفه‌جویی: <Money value={originalRowTotal - rowTotal} /></em>}
                          </div>

                          <Quantity quantity={item.quantity} inc={() => inc(item)} dec={() => dec(item)} max={item.stock || item.available_stock || 0} />
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              </section>

              <aside className="cart-fashion-summary">
                <div className="cart-summary-glow" />

                <div className="cart-fashion-summary-head">
                  <span>
                    <FiShoppingBag size={18} />
                  </span>
                  <div>
                    <h2>خلاصه خرید</h2>
                  </div>
                </div>

                <div className="cart-fashion-summary-lines">
                  <div>
                    <span>تعداد کالا</span>
                    <strong>{toPersianNumber(items)} عدد</strong>
                  </div>

                  <div>
                    <span>مبلغ کالاها</span>
                    <strong><Money value={originalTotal} /></strong>
                  </div>

                  {hasAnyDiscount && (
                    <div className="cart-saving-line">
                      <span>سود شما از تخفیف</span>
                      <strong><Money value={savedAmount} /></strong>
                    </div>
                  )}

                  <div className="cart-shipping-line">
                    <span>ارسال</span>
                    <strong>مرحله بعد</strong>
                  </div>

                  <div className="cart-final-line">
                    <span>جمع سبد</span>
                    <strong><Money value={total} /></strong>
                  </div>
                </div>

                {hasPaymentRecovery && (
                  <div
                    className={`cart-payment-recovery ${
                      recoveryRequiresReview
                        ? "is-review"
                        : "is-ready"
                    }`}
                  >
                    <div className="cart-payment-recovery-main">
                      <span className="cart-payment-recovery-dot" />

                      <div>
                        <strong>
                          {recoveryRequiresReview
                            ? "پرداخت در حال بررسی است"
                            : "پرداخت این سفارش تکمیل نشده"}
                        </strong>

                        <p>
                          {recoveryRequiresReview
                            ? "برای جلوگیری از پرداخت تکراری، تا مشخص شدن نتیجه پرداخت قبلی پرداخت جدیدی ساخته نمی‌شود."
                            : "کالاهای این سفارش هنوز برای شما رزرو هستند و می‌توانید پرداخت را ادامه دهید."}
                        </p>
                      </div>
                    </div>

                    <div className="cart-payment-recovery-meta">
                      {paymentRecovery?.order_number && (
                        <span>
                          سفارش{" "}
                          {toPersianNumber(
                            paymentRecovery.order_number
                          )}
                        </span>
                      )}

                      {reservationUntilLabel && (
                        <span>
                          {reservationUntilLabel}
                        </span>
                      )}
                    </div>

                    {paymentRecovery?.can_abandon ? (
                      <button
                        type="button"
                        className="cart-payment-recovery-edit"
                        onClick={changeRecoveryCart}
                        disabled={
                          abandonPaymentRecoveryResponse.isLoading
                        }
                      >
                        {abandonPaymentRecoveryResponse.isLoading
                          ? "در حال آماده‌سازی..."
                          : "تغییر سبد"}
                      </button>
                    ) : (
                      <p className="cart-payment-recovery-lock-note">
                        تا زمانی که یک پرداخت فعال یا در حال بررسی است،
                        ویرایش سبد برای جلوگیری از پرداخت اشتباه غیرفعال است.
                      </p>
                    )}
                  </div>
                )}

                {shouldShowSnappPay && (
                  <div className="jooti-snapp-component cart-snapp-component">
                    <img src={snappPayLogo} alt="اسنپ‌پی" />
                    <div>
                      <strong>{snappPayTitle}</strong>
                      <span>{snappPayDescription}</span>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={
                    recoveryRequiresReview
                      ? openRecoveryStatus
                      : pay
                  }
                  className="cart-fashion-pay"
                  disabled={
                    validateCartResponse.isLoading
                  }
                >
                  {validateCartResponse.isLoading
                    ? "بررسی..."
                    : recoveryRequiresReview
                      ? "مشاهده وضعیت پرداخت"
                      : hasPaymentRecovery
                        ? "ادامه پرداخت"
                        : "ادامه خرید"}

                  <FiArrowLeft size={18} />
                </button>

                <Link to="/" className="cart-fashion-continue">
                  ادامه خرید
                </Link>
              </aside>
            </div>
          ) : (
            <div className="cart-empty-state cart-fashion-empty">
              <div>
                <FiShoppingBag size={34} />
              </div>

              <h2>سبد خرید شما خالی است</h2>
              <p>محصولات مورد علاقه‌ات را انتخاب کن.</p>

              <Link to="/" className="btn btn-primary">
                بازگشت به فروشگاه
              </Link>
            </div>
          )}
        </div>
      </motion.main>
    </>
  );
};

export default Cart;
